import React, { useState, useMemo } from "react";
import { useLang } from "@/lib/i18n";
import { Search, Filter } from "lucide-react";

const resultStyle = {
  WIN: { bg: "#14532D", color: "#4ADE80", border: "rgba(34,197,94,0.45)", dot: "#4ADE80" },
  LOSS: { bg: "#4A0512", color: "#F87171", border: "rgba(239,68,68,0.5)", dot: "#F87171" },
  DRAW: { bg: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.75)", border: "rgba(255,255,255,0.25)", dot: "#ccc" },
  "NO TIP": { bg: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.5)", border: "rgba(255,255,255,0.18)", dot: "#aaa" },
};

function ResultBadge({ result, label }) {
  const s = resultStyle[result] || resultStyle["NO TIP"];
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full text-[11px] font-bold font-body px-2.5 py-1 whitespace-nowrap border" style={{ background: s.bg, color: s.color, borderColor: s.border }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />{label}
    </span>
  );
}

export default function Archive({ matches }) {
  const { t } = useLang();
  const [search, setSearch] = useState("");
  const [result, setResult] = useState("ALL");
  const [betType, setBetType] = useState("ALL");
  const [visible, setVisible] = useState(20);

  const filtered = useMemo(() => matches.filter((m) => {
    if (search && !m.match.toLowerCase().includes(search.toLowerCase())) return false;
    if (result !== "ALL" && m.result !== result) return false;
    if (betType !== "ALL" && m.bet_type !== betType) return false;
    return true;
  }), [matches, search, result, betType]);

  const shown = filtered.slice(0, visible);
  const cols = t("archive.cols");
  const resultLabels = t("archive.results");
  const selectCls = "px-4 py-2.5 rounded-xl border border-[var(--border-light)] bg-white font-body text-sm font-semibold text-gray-900 focus:outline-none focus:border-[var(--trust-gold)] [&>option]:text-gray-900";

  return (
    <section id="archive" className="py-20 lg:py-28" style={{ background: "var(--at-bg)" }}>
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-2xl mb-10">
          <div className="flex items-center gap-3 mb-4">
            <span className="font-heading text-xs font-extrabold tracking-[0.25em] uppercase gold-gradient-text">06</span>
            <span className="w-8 h-px bg-[var(--trust-gold)]/50" />
            <span className="font-heading text-[11px] font-bold tracking-[0.2em] uppercase text-white/60">{t("archive.kicker")}</span>
          </div>
          <h2 className="font-serif-display font-bold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl leading-tight">{t("archive.title")}</h2>
          <p className="mt-4 text-base font-body text-white/75 leading-relaxed">{t("archive.subtitle")}</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input data-testid="archive-search" value={search} onChange={(e) => { setSearch(e.target.value); setVisible(20); }} placeholder={t("archive.searchPlaceholder")} className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[var(--border-light)] bg-white text-gray-900 placeholder:text-gray-400 font-body text-sm focus:outline-none focus:border-[var(--trust-gold)] transition-colors" />
          </div>
          <div className="flex gap-3">
            <select data-testid="archive-filter-result" value={result} onChange={(e) => { setResult(e.target.value); setVisible(20); }} className={selectCls}>
              <option value="ALL">{t("archive.allResults")}</option>
              <option value="WIN">{resultLabels.WIN}</option>
              <option value="LOSS">{resultLabels.LOSS}</option>
              <option value="DRAW">{resultLabels.DRAW}</option>
            </select>
            <select data-testid="archive-filter-type" value={betType} onChange={(e) => { setBetType(e.target.value); setVisible(20); }} className={selectCls}>
              <option value="ALL">{t("archive.allTypes")}</option>
              <option value="ASIAN HANDICAP">ASIAN HANDICAP</option>
              <option value="OVER / UNDER">OVER / UNDER</option>
            </select>
          </div>
        </div>

        <div className="md:hidden space-y-3" data-testid="archive-mobile-list">
          {shown.length === 0 && <div className="at-card rounded-xl p-6 text-center text-white/60 font-body text-sm">{t("archive.empty")}</div>}
          {shown.map((m, i) => (
            <div key={m.id || i} data-testid={`archive-mrow-${i}`} className="at-card-solid rounded-xl p-4">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] font-body text-white/55 tabular-nums">{m.datetime}</span>
                <ResultBadge result={m.result} label={resultLabels[m.result] || m.result} />
              </div>
              <div className="mt-1.5 font-body text-sm font-semibold text-white leading-snug">{m.match}</div>
              <div className="mt-2.5 flex flex-wrap items-center gap-2">
                {m.bet_type !== "---" && <span className="text-[10px] font-bold font-heading px-2 py-0.5 rounded-md bg-white/10 text-[var(--trust-gold)]">{m.bet_type}</span>}
                {m.tip !== "---" && <span className="text-xs font-body font-medium text-white/85">{m.tip}</span>}
              </div>
              <div className="mt-3 pt-3 border-t border-[var(--border-light)] grid grid-cols-3 gap-2 text-center">
                <div><div className="text-[10px] uppercase tracking-wide text-white/50 font-heading">{cols.odds}</div><div className="font-body text-sm font-bold text-white tabular-nums">{m.odds != null ? m.odds.toFixed(2) : "—"}</div></div>
                <div className="border-x border-[var(--border-light)]"><div className="text-[10px] uppercase tracking-wide text-white/50 font-heading">{cols.score}</div><div className="font-body text-sm text-white/80 tabular-nums">{m.score}</div></div>
                <div><div className="text-[10px] uppercase tracking-wide text-white/50 font-heading">{cols.tipster}</div><div className="font-body text-[11px] text-white/70 leading-tight">Markus Huber</div></div>
              </div>
            </div>
          ))}
        </div>

        <div data-testid="archive-table" className="hidden md:block at-card-solid rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[880px]">
              <thead>
                <tr style={{ background: "var(--at-darkest)" }} className="text-white">
                  {["datetime", "tipster", "match", "betType", "tip", "odds", "score", "result"].map((c) => (
                    <th key={c} className="px-4 py-3.5 font-heading text-[11px] font-bold uppercase tracking-wider whitespace-nowrap text-white/90">{cols[c]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-white/60 font-body text-sm">{t("archive.empty")}</td></tr>}
                {shown.map((m, i) => (
                  <tr key={m.id || i} data-testid={`archive-row-${i}`} className="border-b border-white/8 hover:bg-white/5 transition-colors">
                    <td className="px-4 py-3.5 font-body text-xs text-white/60 whitespace-nowrap tabular-nums">{m.datetime}</td>
                    <td className="px-4 py-3.5 font-body text-sm font-semibold text-white/90 whitespace-nowrap">{m.tipster}</td>
                    <td className="px-4 py-3.5 font-body text-sm text-white font-medium">{m.match}</td>
                    <td className="px-4 py-3.5 whitespace-nowrap">{m.bet_type !== "---" ? <span className="text-[11px] font-bold font-heading px-2 py-1 rounded-md bg-white/10 text-[var(--trust-gold)]">{m.bet_type}</span> : <span className="text-white/30">—</span>}</td>
                    <td className="px-4 py-3.5 font-body text-sm text-white/85 whitespace-nowrap">{m.tip === "---" ? <span className="text-white/30">—</span> : m.tip}</td>
                    <td className="px-4 py-3.5 font-body text-sm font-bold text-white tabular-nums whitespace-nowrap">{m.odds != null ? m.odds.toFixed(2) : <span className="text-white/30">—</span>}</td>
                    <td className="px-4 py-3.5 font-body text-xs text-white/60 tabular-nums whitespace-nowrap">{m.score}</td>
                    <td className="px-4 py-3.5"><ResultBadge result={m.result} label={resultLabels[m.result] || m.result} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex items-center justify-between mt-5 flex-wrap gap-3">
          <div className="text-xs font-body text-white/55 flex items-center gap-1.5"><Filter size={13} /> {shown.length} {t("archive.showing")} {filtered.length}</div>
          {visible < filtered.length && (
            <button data-testid="archive-load-more" onClick={() => setVisible((v) => v + 30)} className="px-6 py-2.5 rounded-full border border-[var(--border-gold)] font-heading text-sm font-bold text-white hover:bg-white/5 transition-colors">{t("archive.showMore")}</button>
          )}
        </div>
      </div>
    </section>
  );
}
